import * as React from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Text,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { Tenant } from './types';

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (tenantIds: string[]) => void;
  subjectName: string; // e.g. "Credit Card Number"
  subjectKind: 'SIT' | 'label' | 'policy';
  tenants: Tenant[];
};

export default function DeployDialog({
  open,
  onClose,
  onConfirm,
  subjectName,
  subjectKind,
  tenants,
}: Props) {
  const [selected, setSelected] = React.useState<string[]>([]);
  React.useEffect(() => {
    if (open) setSelected([]);
  }, [open]);

  const toggle = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const title = `Deploy ${subjectName}`;

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface style={{ maxWidth: '600px', maxHeight: '85vh' }} aria-describedby="deploy-desc">
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="Close dialog"
                icon={<Dismiss24Regular />}
                onClick={onClose}
              />
            }
          >
            {title}
          </DialogTitle>
          <DialogContent id="deploy-desc">
            <Text
              block
              style={{ display: 'block', marginBottom: 16, color: '#6b7280', fontSize: 14 }}
            >
              Select the tenants to deploy <strong>{subjectName}</strong> to. Tenants where it is
              already available are shown but cannot be re-selected.
            </Text>
            <div
              style={{
                border: '1px solid #e5e5e5',
                borderRadius: 4,
                maxHeight: 400,
                overflowY: 'auto',
              }}
            >
              {tenants.map((t, i) => {
                const isAvailable = t.status === 'Available';
                return (
                  <div
                    key={t.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px 16px',
                      borderBottom: i === tenants.length - 1 ? 'none' : '1px solid #f0f0f0',
                      gap: 12,
                      opacity: isAvailable ? 0.65 : 1,
                    }}
                  >
                    <Checkbox
                      aria-label={`Select ${t.name}`}
                      checked={selected.includes(t.id)}
                      disabled={isAvailable}
                      onChange={() => toggle(t.id)}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                        {t.name}
                        {subjectKind === 'label' && (
                          <span
                            style={{
                              padding: '1px 6px',
                              borderRadius: 4,
                              fontSize: 11,
                              fontWeight: 700,
                              background: '#eff6fc',
                              color: '#0078d4',
                              border: '1px solid #0078d4',
                            }}
                            title="Parent and standalone labels are deployed with this prefix; child labels are not prefixed."
                          >
                            Prefix: {t.prefix || '—'}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{t.id}</div>
                    </div>
                    {isAvailable ? (
                      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#e6f4ea', color: '#1a7f37' }}>
                        Available
                      </span>
                    ) : (
                      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#f3f2f1', color: '#605e5c' }}>
                        Not Deployed
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={selected.length === 0}
              onClick={() => onConfirm(selected)}
            >
              Deploy in {selected.length} tenant(s)
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
